import React from 'react';
import { shallowEqual } from 'react-redux';
import { useResponsiveTableContext } from '../ResponsiveTableContext';
import { TResponsiveTableColumn } from '../types';
import { AccordionElement } from './AccordionElement';
import { NoRegistersRenderer } from './NoRegistersRenderer';
import {
  responsiveTableActions,
  useResponsiveTable,
  responsiveTableStore,
} from '../store';
import { TResponsiveTableIndexColumns } from '../store/types';
import { Accordion } from '@apia/components';

const NoMemoAccordionRenderer = () => {
  const { name } = useResponsiveTableContext();

  const { columns, rows } = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return {
      columns: tableState?.columns,
      rows: tableState?.rows,
    };
  }, shallowEqual);

  React.useEffect(() => {
    /**
     * Método para seleccionar qué columnas se utilizarán
     * como cabezal en cada fila.
     *
     * - Si existe al menos una columna marcada con showInAccordionTitle,
     * todas las columnas que estén marcadas con esta propiedad serán
     * mostradas como parte del título.
     *
     * - Si no existe ninguna, se busca cuál es la primera columna
     * que no tiene ninguna entrada vacía y se utiliza como título
     * del acordión.
     *
     * - Si ninguna columna no tiene ninguna fila vacía, entonces se
     * utiliza la primera columna.
     */

    if (columns && rows) {
      /**
       * Buscar columnas marcadas
       */
      const markedAsTitleColumns: TResponsiveTableIndexColumns = [];
      columns.forEach((column, index) => {
        if (column.showInAccordionTitle)
          markedAsTitleColumns.push({ column, index });
      });
      if (markedAsTitleColumns.length > 0) {
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: name,
            accordionIndexColumns: markedAsTitleColumns,
          }),
        );
      } else {
        /**
         * No se encontró ninguna columna marcada, se busca la primera sin
         * entradas vacías.
         */
        const columnsLength = columns.length;
        let indexColumn: TResponsiveTableColumn | null = null;
        let currentIndex = 0;

        while (!indexColumn && currentIndex < columns.length) {
          let isAnyEmptyRow = false;
          // eslint-disable-next-line no-restricted-syntax
          for (const row of rows) {
            if (!row.cells[currentIndex].children) {
              isAnyEmptyRow = true;
              break;
            }
          }
          if (!isAnyEmptyRow && columns[currentIndex]) {
            indexColumn = columns[currentIndex];
          } else {
            currentIndex += 1;
            if (currentIndex === columnsLength) break;
          }
        }
        if (!indexColumn) {
          /**
           * No hay ninguna sin ninguna entrada vacía,
           * se usa la primera columna.
           */
          [indexColumn] = columns;
          currentIndex = 0;
        }
        responsiveTableStore.dispatch(
          responsiveTableActions.update({
            tableName: name,
            accordionIndexColumns: [
              { column: indexColumn, index: currentIndex },
            ],
          }),
        );
      }
    }
  }, [columns, name, rows]);

  return (
    <Accordion>
      {!rows || (rows.length === 0 && <NoRegistersRenderer />)}
      {rows?.map((current, rowIndex) => (
        <AccordionElement row={current} rowIndex={rowIndex} key={current.id} />
      ))}
    </Accordion>
  );
};

export const AccordionRenderer = React.memo(NoMemoAccordionRenderer);
